import { Box, TextField } from "@mui/material";
import { Contact, EmailType, PhoneType } from "@repo/server-api";
import { EmailInformation, PhoneInformation } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import * as Yup from "yup";

type Exposed = {
	validate: () => Promise<Record<string, boolean | object | string>>;
};

function deriveInfo(contact: Contact | null) {
	if (!contact) return { phone: "", email: "" };
	const phone = contact.additionalInformation.find(
		(i): i is PhoneInformation =>
			(i as PhoneInformation).__typename === "PhoneInformation" &&
			(i as PhoneInformation).phoneType === PhoneType.Work,
	);
	const email = contact.additionalInformation.find(
		(i): i is EmailInformation => (i as EmailInformation).__typename === "EmailInformation",
	);
	const mobile = contact.additionalInformation.find(
		(i): i is PhoneInformation =>
			(i as PhoneInformation).__typename === "PhoneInformation" &&
			(i as PhoneInformation).phoneType === PhoneType.Mobile,
	);
	return {
		phone: phone ? phone.number : "",
		email: email ? email.emailAddress : "",
		mobile: mobile ? mobile.number : "",
	};
}

const ContactInfoInner = forwardRef(function ContactInfo(
	props: Readonly<{
		contact: Contact | null;
		setContact: (value: Contact | null) => void;
		errors?: Record<string, boolean | object | string>;
		setErrors?: (value: Record<string, boolean | object | string>) => void;
	}>,
	ref: React.Ref<Exposed>,
) {
	const { t } = useTranslation();

	const info = useMemo(() => deriveInfo(props.contact), [props.contact]);

	const [emailLocal, setEmailLocal] = useState(info.email);
	const [phoneLocal, setPhoneLocal] = useState(info.phone);
	const [mobileLocal, setMobileLocal] = useState(info.mobile);

	const emailFocusedRef = useRef(false);
	const phoneFocusedRef = useRef(false);
	const mobileFocusedRef = useRef(false);

	useEffect(() => {
		if (!emailFocusedRef.current) setEmailLocal(info.email);
	}, [info.email]);

	useEffect(() => {
		if (!phoneFocusedRef.current) setPhoneLocal(info.phone);
	}, [info.phone]);

	useEffect(() => {
		if (!mobileFocusedRef.current) setMobileLocal(info.mobile);
	}, [info.mobile]);

	const setEmailInContact = (email: string) => {
		if (!props.contact) return;
		const emailInfo = props.contact.additionalInformation.filter(
			(i): i is EmailInformation => (i as EmailInformation).__typename === "EmailInformation",
		);
		const emailObject: EmailInformation = {
			id: emailInfo.length ? emailInfo[0].id : "",
			__typename: "EmailInformation",
			emailAddress: email,
			emailType: EmailType.Work,
		} as const;

		props.setContact({
			...props.contact,
			id: props.contact.id ?? "",
			additionalInformation: emailInfo.length
				? props.contact.additionalInformation.map((i) =>
						(i as EmailInformation).__typename === "EmailInformation" ? { ...emailObject } : i,
					)
				: [...props.contact.additionalInformation, { ...emailObject }],
		});
	};

	const setPhoneInContact = (phone: string) => {
		if (!props.contact) return;
		const phoneInfo = props.contact.additionalInformation.filter(
			(i): i is PhoneInformation =>
				(i as PhoneInformation).__typename === "PhoneInformation" &&
				(i as PhoneInformation).phoneType === PhoneType.Work,
		);
		const phoneObject: PhoneInformation = {
			id: phoneInfo.length ? phoneInfo[0].id : "",
			__typename: "PhoneInformation",
			number: phone,
			phoneType: PhoneType.Work,
		} as const;

		props.setContact({
			...props.contact,
			id: props.contact.id ?? "",
			additionalInformation: phoneInfo.length
				? props.contact.additionalInformation.map((i) =>
						(i as PhoneInformation).__typename === "PhoneInformation" &&
						(i as PhoneInformation).phoneType === PhoneType.Work
							? { ...phoneObject }
							: i,
					)
				: [...props.contact.additionalInformation, { ...phoneObject }],
		});
	};

	const setMobileInContact = (mobile: string) => {
		if (!props.contact) return;
		const mobileInfo = props.contact.additionalInformation.filter(
			(i): i is PhoneInformation =>
				(i as PhoneInformation).__typename === "PhoneInformation" &&
				(i as PhoneInformation).phoneType === PhoneType.Mobile,
		);
		const mobileObject: PhoneInformation = {
			id: mobileInfo.length ? mobileInfo[0].id : "",
			__typename: "PhoneInformation",
			number: mobile,
			phoneType: PhoneType.Mobile,
		} as const;

		props.setContact({
			...props.contact,
			id: props.contact.id ?? "",
			additionalInformation: mobileInfo.length
				? props.contact.additionalInformation.map((i) =>
						(i as PhoneInformation).__typename === "PhoneInformation" &&
						(i as PhoneInformation).phoneType === PhoneType.Mobile
							? { ...mobileObject }
							: i,
					)
				: [...props.contact.additionalInformation, { ...mobileObject }],
		});
	};

	const validationSchema = Yup.object().shape({
		email: Yup.string()
			.matches(
				/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.\-_]+\.[a-zA-Z]{2,}$/,
				t("email.invalid", { ns: "validation" }),
			)
			.required(t("email.required", { ns: "validation" })),
	});

	const validateFields = useCallback(async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(
				{ email: emailLocal, phone: phoneLocal },
				{ abortEarly: false },
			);
			props.setErrors?.({});
			return localErrors;
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((e) => {
					if (e.path) localErrors[e.path] = e.message;
				});
			}
			props.setErrors?.({ ...localErrors });
			return localErrors;
		}
	}, [emailLocal, phoneLocal, props, validationSchema]);

	useImperativeHandle(ref, () => ({ validate: validateFields }), [validateFields]);

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const v = e.target.value ?? "";
		setEmailLocal(v);
		setEmailInContact(v);
		props.setErrors?.({ ...props.errors, email: "" });
	};

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const v = e.target.value ?? "";
		setPhoneLocal(v);
		setPhoneInContact(v);
	};

	const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const v = e.target.value ?? "";
		setMobileLocal(v);
		setMobileInContact(v);
	};

	return (
		<div>
			<Box className="fullCenter">
				<TextField
					value={emailLocal}
					onChange={handleEmailChange}
					onFocus={() => {
						emailFocusedRef.current = true;
					}}
					onBlur={() => {
						emailFocusedRef.current = false;
						setEmailLocal(deriveInfo(props.contact).email);
					}}
					label={t("email")}
					variant="outlined"
					className="w-100"
					error={!!props.errors?.email}
					helperText={props.errors?.email as string}
				/>
			</Box>
			<Box className="fullCenter">
				<TextField
					value={phoneLocal}
					onChange={handlePhoneChange}
					onFocus={() => {
						phoneFocusedRef.current = true;
					}}
					onBlur={() => {
						phoneFocusedRef.current = false;
						setPhoneLocal(deriveInfo(props.contact).phone);
					}}
					label={t("phone")}
					variant="outlined"
					className="w-100"
					sx={{ mt: 2 }}
				/>
			</Box>
			<Box className="fullCenter">
				<TextField
					value={mobileLocal}
					onChange={handleMobileChange}
					onFocus={() => {
						mobileFocusedRef.current = true;
					}}
					onBlur={() => {
						mobileFocusedRef.current = false;
						setMobileLocal(deriveInfo(props.contact).mobile);
					}}
					label={t("mobile")}
					variant="outlined"
					className="w-100"
					sx={{ mt: 2 }}
				/>
			</Box>
		</div>
	);
});

const ContactInfo = memo(ContactInfoInner);
export default ContactInfo;
